/**
 * Central translations file — English (en), Bahasa Malaysia (bm), and Chinese (cn).
 *
 * How to add BM / CN text:
 *   1. Find the page section below (e.g. `bm.ageSmoker` or `cn.ageSmoker`).
 *   2. Replace the English string with the target-language equivalent.
 *   3. The `useTranslation()` hook will automatically pick the right copy.
 *
 * Any BM/CN string left identical to EN is just a placeholder — update it when you have the copy.
 */

export type Lang = 'en' | 'bm' | 'cn';

// ─── Shape ───────────────────────────────────────────────────────────────────

export type AppTranslations = {
  home: {
    brownLine1: string;
    brownLine2: string;
    brownLine3: string;
    brownLine4: string;
    brownLine5: string;
    brownLine6: string;
  };
  ageSmoker: {
    title: string;
    smokerQuestion: string;
    yes: string;
    no: string;
    submit: string;
    allRequiredWarning: string;
  };
  pdpa: {
    title: string;
    consentText: string;
    agree: string;
    decline: string;
    consentWarning: string;
    noticeTitle: string;
  };
  uniqueCode: {
    instruction: string;
    mobileLabel: string;
    uniqueCodeLabel: string;
    findCodeHint: string;
    requestOtpButton: string;
    mobileWarning: string;
    uniqueCodeWarning: string;
    codeUsedWarning: string;
    codeNotFoundWarning: string;
    verifyError: string;
    otpError: string;
    turnstileFailed: string;
    confirmTitle: string;
    confirmMobileLabel: string;
    confirmUniqueCodeLabel: string;
    confirmPrompt: string;
    verifyingSecurity: string;
    back: string;
    confirm: string;
    monthlyLimitMessage: string;
    monthlyLimitClose: string;
    dailyLimitMessage: string;
  };
  otp: {
    sentMessage: string;
    otpCodeLabel: string;
    submit: string;
    emptyWarning: string;
    verifyError: string;
    /** Use {{seconds}} placeholder for countdown whole seconds. */
    waitBeforeSubmit: string;
    readyToSubmit: string;
  };
  smokerProfile: {
    nameLabel: string;
    namePlaceholder: string;
    emailLabel: string;
    emailPlaceholder: string;
    emailWarning: string;
    nationalityLabel: string;
    selectNationality: string;
    nationalityMalaysian: string;
    nationalityNonMalaysian: string;
    countryLabel: string;
    selectCountry: string;
    raceLabel: string;
    stateLabel: string;
    selectRace: string;
    selectState: string;
    /** Race dropdown display labels (canonical EN values stored separately). */
    raceMalay: string;
    raceChinese: string;
    raceIndian: string;
    raceOthers: string;
    /** State dropdown display labels (canonical EN values stored separately). */
    stateJohor: string;
    stateKedah: string;
    stateKelantan: string;
    stateKualaLumpur: string;
    stateMelaka: string;
    stateNegeriSembilan: string;
    statePahang: string;
    statePerak: string;
    statePerlis: string;
    statePulauPinang: string;
    stateSabah: string;
    stateSarawak: string;
    stateSelangor: string;
    stateTerengganu: string;
    brandLabel: string;
    selectBrand: string;
    loadingBrands: string;
    selectBrandFirst: string;
    selectVariant: string;
    loadingVariants: string;
    brandsLoadingWarning: string;
    variantsLoadingWarning: string;
    brandsLoadError: string;
    continue: string;
    incompleteWarning: string;
  };
  otherBrands: {
    question: string;
    loading: string;
    loadError: string;
    requiredWarning: string;
    otherSpecify: string;
    otherSpecifyWarning: string;
    continue: string;
  };
  q7_1: {
    question: string;
    /** Dice-only /7_1 question. */
    diceQuestion: string;
    optionSeenAtStore: string;
    optionHeardFromSomeone: string;
    optionSeenOnline: string;
    optionSeenPackBefore: string;
    optionDontRemember: string;
  };
  q7_2: {
    question: string;
    /** Dice-only /7_2 question (like-most). */
    diceQuestion: string;
    /** Dice /7_2 option display labels (canonical EN stored separately). */
    optionTasteFlavour: string;
    optionOverallExperience: string;
    optionAppearancePack: string;
    optionSomethingDifferent: string;
    optionNothingInParticular: string;
    yourAnswer: string;
    selectPlaceholder: string;
    loading: string;
    loadError: string;
    requiredWarning: string;
    others: string;
    otherSpecifyPlaceholder: string;
    otherSpecifyWarning: string;
    continue: string;
  };
  q7_3: {
    question: string;
    /** Dice-only /7_3 question (what could improve). */
    diceQuestion: string;
    /** LBS /7_3 next-purchase option display labels (canonical EN stored separately). */
    optionChooseAgain: string;
    optionGoBackUsual: string;
    optionTryAnother: string;
    optionDependOccasion: string;
    /** Dice /7_3 option display labels (canonical EN stored separately). */
    optionTasteFlavour: string;
    optionOverallExperience: string;
    optionStrengthIntensity: string;
    optionAppearancePack: string;
    optionFormat: string;
    optionAvailability: string;
    optionNothingInParticular: string;
  };
  q7_4: {
    question: string;
    /** Dice-only /7_4 question (mention likelihood) — final Dice step. */
    diceQuestion: string;
    /** LBS /7_4 like-most option display labels (canonical EN stored separately). */
    optionTasteFlavour: string;
    optionOverallExperience: string;
    optionAppearancePack: string;
    optionSomethingDifferent: string;
    optionNothingInParticular: string;
    /** Dice /7_4 likelihood option display labels (canonical EN stored separately). */
    optionVeryLikely: string;
    optionSomewhatLikely: string;
    optionNotVeryLikely: string;
    optionNotAtAllLikely: string;
    optionNotSure: string;
    submit: string;
    selectWarning: string;
    sessionExpiredError: string;
    mobileError: string;
    incompleteError: string;
    saveError: string;
  };
  q7_5: {
    question: string;
    optionTasteFlavour: string;
    optionOverallExperience: string;
    optionStrengthIntensity: string;
    optionAppearancePack: string;
    optionFormat: string;
    optionAvailability: string;
    optionNothingInParticular: string;
  };
  q7_6: {
    question: string;
    optionVeryLikely: string;
    optionSomewhatLikely: string;
    optionNotVeryLikely: string;
    optionNotAtAllLikely: string;
    optionNotSure: string;
    submit: string;
    selectWarning: string;
    sessionExpiredError: string;
    mobileError: string;
    incompleteError: string;
    saveError: string;
  };
  devReg: {
    question: string;
    optionYes: string;
    optionCareContact: string;
    optionNoRegister: string;
  };
  tradeIn: {
    question: string;
    optionYesHelp: string;
    optionNotInterested: string;
    optionAlreadyNew: string;
    tnc: string;
    sessionExpiredError: string;
    mobileError: string;
    incompleteError: string;
    saveError: string;
  };
  tradeInContact: {
    instruction: string;
    mobileLabel: string;
    nameLabel: string;
    emailLabel: string;
    submit: string;
    mobileWarning: string;
    nameWarning: string;
    emailWarning: string;
    sessionExpiredWarning: string;
    incompleteWarning: string;
    pdpaNote: string;
    saveError: string;
  };
  thankYou: {
    title: string;
    subtitle: string;
  };
  tng: {
    title: string;
    subtitle: string;
    smsNote: string;
    /** Short label beside the PIN copy icon (e.g. Copy / salin / 复制). */
    copyLabel: string;
    copyPin: string;
    copiedPin: string;
  };
};

// ─── English ─────────────────────────────────────────────────────────────────

const en: AppTranslations = {
  home: {
    brownLine1: 'Respondents who complete the',
    brownLine2: 'survey will be provided a token',
    brownLine3: "of appreciation.* T&C apply.",
    brownLine4: 'Choose your language to',
    brownLine5: 'proceed if you agree to',
    brownLine6: 'participate in the survey.',
  },
  ageSmoker: {
    title: 'Age',
    smokerQuestion: 'Are you a smoker?',
    yes: 'Yes',
    no: 'No',
    submit: 'Submit',
    allRequiredWarning: 'Please answer all questions before continuing to the next page.',
  },
  pdpa: {
    title: 'PDPA',
    consentText: 'I hereby confirm my consent to the processing of my personal data as set out above.',
    agree: 'Agree',
    decline: 'Decline',
    consentWarning: 'Please tick the terms and condition to continue.',
    noticeTitle: 'NOTICE UNDER THE PERSONAL DATA PROTECTION ACT 2010 ("PDPA")',
  },
  uniqueCode: {
    instruction: 'Please input your mobile number and survey unique code for us to send you an OTP.',
    mobileLabel: 'Mobile No.:',
    uniqueCodeLabel: 'Unique Code:',
    findCodeHint: '*Find the Unique Code behind the QR sticker.',
    requestOtpButton: 'Request OTP',
    mobileWarning: 'Please enter your mobile number.',
    uniqueCodeWarning: 'Please enter your unique code.',
    codeUsedWarning: 'This unique code has already been redeemed.',
    codeNotFoundWarning: 'Unique code not found',
    verifyError: 'Unable to verify unique code. Please try again.',
    otpError: 'Unable to send OTP. Please try again.',
    turnstileFailed:
      'Security check could not complete. Please check your internet connection and try again.',
    confirmTitle: 'You have entered:',
    confirmMobileLabel: 'Mobile No: ',
    confirmUniqueCodeLabel: 'Unique Code: ',
    confirmPrompt: 'Please confirm the information is correct.',
    verifyingSecurity: 'Security verification in progress. Please wait...',
    back: 'Back',
    confirm: 'Confirm',
    monthlyLimitMessage:
      "Thank you for taking part in our survey! You've reached your participation limit for this month. Please come back next month to participate again.",
    monthlyLimitClose: 'Close',
    dailyLimitMessage: 'Daily redemption limit reached for this phone number.',
  },
  otp: {
    sentMessage: 'OTP has been sent to your mobile no.:',
    otpCodeLabel: 'OTP Code:',
    submit: 'Submit',
    emptyWarning: 'Please enter the OTP code.',
    verifyError: 'Unable to verify OTP. Please try again.',
    waitBeforeSubmit:
      'Wait {{seconds}}s before tapping Submit. You can type the code while you wait — do not tap Submit repeatedly.',
    readyToSubmit: 'You can tap Submit now.',
  },
  smokerProfile: {
    nameLabel: 'Name',
    namePlaceholder: 'Enter your name',
    emailLabel: 'Email',
    emailPlaceholder: 'Enter your email',
    emailWarning: 'Please enter a valid email address.',
    nationalityLabel: 'Nationality',
    selectNationality: 'Select nationality',
    nationalityMalaysian: 'Malaysian',
    nationalityNonMalaysian: 'Non-Malaysian',
    countryLabel: 'Country',
    selectCountry: 'Select country',
    raceLabel: 'Race',
    stateLabel: 'State of Residence',
    selectRace: 'Select race',
    selectState: 'Select state',
    raceMalay: 'Malay',
    raceChinese: 'Chinese',
    raceIndian: 'Indian',
    raceOthers: 'Others',
    stateJohor: 'Johor',
    stateKedah: 'Kedah',
    stateKelantan: 'Kelantan',
    stateKualaLumpur: 'Kuala Lumpur',
    stateMelaka: 'Melaka',
    stateNegeriSembilan: 'Negeri Sembilan',
    statePahang: 'Pahang',
    statePerak: 'Perak',
    statePerlis: 'Perlis',
    statePulauPinang: 'Pulau Pinang',
    stateSabah: 'Sabah',
    stateSarawak: 'Sarawak',
    stateSelangor: 'Selangor',
    stateTerengganu: 'Terengganu',
    brandLabel: 'Current Smoking Brand',
    selectBrand: 'Select a brand',
    loadingBrands: 'Loading…',
    selectBrandFirst: 'Select a brand first',
    selectVariant: 'Select a variant',
    loadingVariants: 'Loading…',
    brandsLoadingWarning: 'Please wait for the brand list to finish loading.',
    variantsLoadingWarning: 'Please wait for the variant list to finish loading.',
    brandsLoadError: 'Unable to load brands. Please try again.',
    continue: 'Continue',
    incompleteWarning: 'Please answer all questions before continuing.',
  },
  otherBrands: {
    question: 'What is your previous smoking brand?',
    loading: 'Loading brands...',
    loadError: 'Unable to load brands. Please try again.',
    requiredWarning: 'Please choose a brand variant before continuing.',
    otherSpecify: 'Others. Please specify:',
    otherSpecifyWarning: 'Please specify your brand.',
    continue: 'Continue',
  },
  q7_1: {
    question: 'How did you first discover this product?',
    diceQuestion: 'How did you first discover this product?',
    optionSeenAtStore: 'Seen it at a store/outlet',
    optionHeardFromSomeone: 'Heard about it from someone I know',
    optionSeenOnline: 'Seen it online',
    optionSeenPackBefore: 'Seen the pack/product before',
    optionDontRemember: "Don't remember",
  },
  q7_2: {
    question: 'Before trying this product, what did you usually smoke?',
    diceQuestion: 'What do you like most about this product?',
    optionTasteFlavour: 'Taste/flavour',
    optionOverallExperience: 'Overall experience',
    optionAppearancePack: 'Appearance/pack',
    optionSomethingDifferent: 'Something different from my usual choice',
    optionNothingInParticular: 'Nothing in particular',
    yourAnswer: 'Your Answer:',
    selectPlaceholder: 'Select a brand / variant',
    loading: 'Loading brands...',
    loadError: 'Unable to load brands. Please try again.',
    requiredWarning: 'Please select an option before continuing.',
    others: 'Others',
    otherSpecifyPlaceholder: 'Please specify',
    otherSpecifyWarning: 'Please specify what you usually smoked.',
    continue: 'Continue',
  },
  q7_3: {
    question: 'What would you choose for your next purchase?',
    diceQuestion: 'What could be improved about this product?',
    optionChooseAgain: 'Choose this one again',
    optionGoBackUsual: 'Go back to my usual choice',
    optionTryAnother: 'Try another option',
    optionDependOccasion: 'Decide depending on the occasion',
    optionTasteFlavour: 'Taste/flavour',
    optionOverallExperience: 'Overall experience',
    optionStrengthIntensity: 'Strength/intensity',
    optionAppearancePack: 'Appearance/pack',
    optionFormat: 'Format',
    optionAvailability: 'Availability',
    optionNothingInParticular: 'Nothing in particular',
  },
  q7_4: {
    question: 'What do you like most about this product?',
    diceQuestion: 'If asked, how likely are you to mention this product?',
    optionTasteFlavour: 'Taste/flavour',
    optionOverallExperience: 'Overall experience',
    optionAppearancePack: 'Appearance/pack',
    optionSomethingDifferent: 'Something different from my usual choice',
    optionNothingInParticular: 'Nothing in particular',
    optionVeryLikely: 'Very likely',
    optionSomewhatLikely: 'Somewhat likely',
    optionNotVeryLikely: 'Not very likely',
    optionNotAtAllLikely: 'Not at all likely',
    optionNotSure: 'Not sure',
    submit: 'Submit',
    selectWarning: 'Please select one option before submitting.',
    sessionExpiredError: 'Session expired. Please start again from the unique code step.',
    mobileError: 'Mobile number is missing. Please start again from the unique code step.',
    incompleteError: 'Survey data is incomplete. Please go back and complete the previous steps.',
    saveError: 'Unable to save. Please try again.',
  },
  q7_5: {
    question: 'What could be improved about this product?',
    optionTasteFlavour: 'Taste/flavour',
    optionOverallExperience: 'Overall experience',
    optionStrengthIntensity: 'Strength/intensity',
    optionAppearancePack: 'Appearance/pack',
    optionFormat: 'Format',
    optionAvailability: 'Availability',
    optionNothingInParticular: 'Nothing in particular',
  },
  q7_6: {
    question: 'If asked, how likely are you to mention this product?',
    optionVeryLikely: 'Very likely',
    optionSomewhatLikely: 'Somewhat likely',
    optionNotVeryLikely: 'Not very likely',
    optionNotAtAllLikely: 'Not at all likely',
    optionNotSure: 'Not sure',
    submit: 'Submit',
    selectWarning: 'Please select one option before submitting.',
    sessionExpiredError: 'Session expired. Please start again from the unique code step.',
    mobileError: 'Mobile number is missing. Please start again from the unique code step.',
    incompleteError: 'Survey data is incomplete. Please go back and complete the previous steps.',
    saveError: 'Unable to save. Please try again.',
  },
  devReg: {
    question: 'Have you registered your device?',
    optionYes: 'Yes',
    optionCareContact: 'No, I would like the Customer Care Team to contact me for the device registration',
    optionNoRegister: "No, I don't want to register my device",
  },
  tradeIn: {
    question: 'Would you like to trade in your Old Device?',
    optionYesHelp: 'Yes, I would like the Consumer Care Team to help me with the Device trade-in process.',
    optionNotInterested: 'No, I am not interested to trade-in my Device right now.',
    optionAlreadyNew: 'No, I am already using the new Device',
    tnc: '* Terms and conditions apply',
    sessionExpiredError: 'Session expired. Please start again from the unique code step.',
    mobileError: 'Mobile number is missing. Please go back to the unique code step.',
    incompleteError: 'Survey data is incomplete. Please go back and complete the previous steps.',
    saveError: 'Unable to save. Please try again.',
  },
  tradeInContact: {
    instruction: 'Kindly complete your information in the fields below.',
    mobileLabel: 'Mobile No.:',
    nameLabel: 'Name:',
    emailLabel: 'Email Address:',
    submit: 'Submit',
    mobileWarning: 'Please enter your mobile number.',
    nameWarning: 'Please enter your name.',
    emailWarning: 'Please enter a valid email address.',
    sessionExpiredWarning: 'Session expired. Please start again from the unique code step.',
    incompleteWarning: 'Survey data is incomplete. Please go back and complete the previous steps.',
    pdpaNote: '* By submitting your personal information, you agree and consent for us to use, collect and process your personal data for trade-in programme subjecting to the terms of our PDPA notice.',
    saveError: 'Unable to save. Please try again.',
  },
  thankYou: {
    title: 'THANK YOU',
    subtitle: 'FOR YOUR PARTICIPATION',
  },
  tng: {
    title: 'THANK YOU',
    subtitle: 'FOR YOUR PARTICIPATION',
    smsNote: 'Tap ‘Copy’ to copy your TNG PIN, then top up your Touch ’n Go eWallet.',
    copyLabel: 'Copy',
    copyPin: 'Copy PIN',
    copiedPin: 'Copied!',
  },
};

// ─── Bahasa Malaysia ─────────────────────────────────────────────────────────
// TODO: Replace each string below with the correct Bahasa Malaysia translation.
// Strings still matching EN are placeholders — update them as BM copy is provided.

const bm: AppTranslations = {
  home: {
    brownLine1: 'Respondents who complete the',
    brownLine2: 'survey will be provided a token',
    brownLine3: "of appreciation.* T&C apply.",
    brownLine4: 'Choose your language to',
    brownLine5: 'proceed if you agree to',
    brownLine6: 'participate in the survey.',
  },
  ageSmoker: {
    title: 'Umur',
    smokerQuestion: 'Adakah anda seorang perokok?', 
    yes: 'Ya', 
    no: 'Tidak', 
    submit: 'Hantar', 
    allRequiredWarning: 'Sila jawab semua soalan sebelum meneruskan ke halaman seterusnya.', 
  },
  pdpa: {
    title: 'PDPA', 
    consentText: 'Dengan ini saya bersetuju dan mengesahkan persetujuan saya untuk memprocess data peribadi saya seperti dinyatakan di atas.', 
    agree: 'Setuju', 
    decline: 'Tidak Setuju', 
    consentWarning: 'Sila tandakan kotak pilihan untuk mengesahkan persetujuan anda', 
    noticeTitle: 'NOTIS DI BAWAH AKTA PERLINDUNGAN DATA PERIBADI 2010 (“PDPA")', 
  },
  uniqueCode: {
    instruction: 'Sila masukkan no. telefon dan & kod unik untuk menerima OTP', 
    mobileLabel: 'Telefon No.:', 
    uniqueCodeLabel: 'Kod Unik:', 
    findCodeHint: '* Cari kod unik di sebalik pelekat QR', 
    requestOtpButton: 'Minta OTP', 
    mobileWarning: 'Sila masukkan nombor telefon.', 
    uniqueCodeWarning: 'Sila masukkan kod unik.', 
    codeUsedWarning: 'Kod unik ini telah ditebus.', 
    codeNotFoundWarning: 'Kod unik tidak ditemui', 
    verifyError: 'Tidak dapat mengesahkan kod unik. Sila cuba lagi.', 
    otpError: 'Tidak dapat menghantar OTP. Sila cuba lagi.', 
    turnstileFailed:
      'Pemeriksaan keselamatan tidak dapat diselesaikan. Sila semak sambungan internet anda dan cuba lagi.',
    confirmTitle: 'Anda telah memasukkan:', 
    confirmMobileLabel: 'Telefon No:', 
    confirmUniqueCodeLabel: 'Kod Unik', 
    confirmPrompt: 'Sila sahkan maklumat tersebut adalah betul.', 
    verifyingSecurity: 'Pengesahan keselamatan sedang diproses. Sila tunggu...',
    back: 'Kembali', 
    confirm: 'Sahkan',
    monthlyLimitMessage:
      'Terima kasih kerana menyertai tinjauan kami! Anda telah mencapai had penyertaan untuk bulan ini. Sila kembali pada bulan hadapan untuk menyertai lagi.',
    monthlyLimitClose: 'Tutup',
    dailyLimitMessage:
      'Had penebusan harian untuk nombor telefon ini telah dicapai.',
  },
  otp: {
    sentMessage: 'OTP telah dihantar ke nombor telefon bimbit anda:', 
    otpCodeLabel: 'Kod OTP:', 
    submit: 'Hantar', 
    emptyWarning: 'Sila masukkan kod OTP.', 
    verifyError: 'Tidak dapat mengesahkan OTP. Sila cuba lagi.', 
    waitBeforeSubmit:
      'Tunggu {{seconds}} saat sebelum tekan Hantar. Anda boleh masukkan kod sementara menunggu — jangan tekan Hantar berulang kali.',
    readyToSubmit: 'Anda boleh tekan Hantar sekarang.',
  },
  smokerProfile: {
    nameLabel: 'Nama',
    namePlaceholder: 'Masukkan nama anda',
    emailLabel: 'Emel',
    emailPlaceholder: 'Masukkan emel anda',
    emailWarning: 'Sila masukkan alamat emel yang sah.',
    nationalityLabel: 'Kewarganegaraan',
    selectNationality: 'Kewarganegaraan',
    nationalityMalaysian: 'Warganegara Malaysia',
    nationalityNonMalaysian: 'Bukan Warga Malaysia',
    countryLabel: 'Negara',
    selectCountry: 'Pilih negara',
    raceLabel: 'Bangsa',
    stateLabel: 'Negeri Kediaman',
    selectRace: 'Pilih bangsa',
    selectState: 'Pilih negeri',
    raceMalay: 'Melayu',
    raceChinese: 'Cina',
    raceIndian: 'India',
    raceOthers: 'Lain-lain',
    stateJohor: 'Johor',
    stateKedah: 'Kedah',
    stateKelantan: 'Kelantan',
    stateKualaLumpur: 'Kuala Lumpur',
    stateMelaka: 'Melaka',
    stateNegeriSembilan: 'Negeri Sembilan',
    statePahang: 'Pahang',
    statePerak: 'Perak',
    statePerlis: 'Perlis',
    statePulauPinang: 'Pulau Pinang',
    stateSabah: 'Sabah',
    stateSarawak: 'Sarawak',
    stateSelangor: 'Selangor',
    stateTerengganu: 'Terengganu',
    brandLabel: 'Jenama Rokok Semasa Anda',
    selectBrand: 'Pilih jenama',
    loadingBrands: 'Memuatkan…',
    selectBrandFirst: 'Pilih jenama dulu',
    selectVariant: 'Pilih varian',
    loadingVariants: 'Memuatkan…',
    brandsLoadingWarning: 'Sila tunggu sehingga senarai jenama selesai dimuatkan.',
    variantsLoadingWarning: 'Sila tunggu sehingga senarai varian selesai dimuatkan.',
    brandsLoadError: 'Tidak dapat memuatkan jenama. Sila cuba lagi.',
    continue: 'Teruskan',
    incompleteWarning: 'Sila jawab semua soalan sebelum meneruskan.',
  },
  otherBrands: {
    question: 'Apakah jenama rokok anda sebelum ini?',
    loading: 'Memuatkan jenama...',
    loadError: 'Tidak dapat memuatkan jenama. Sila cuba lagi.',
    requiredWarning: 'Sila pilih varian jenama sebelum meneruskan.',
    otherSpecify: 'Lain-lain. Sila nyatakan:',
    otherSpecifyWarning: 'Sila nyatakan jenama anda.',
    continue: 'Teruskan',
  },
  q7_1: {
    question: 'Bagaimana anda mula-mula tahu tentang produk ini?',
    diceQuestion: 'Bagaimana anda mula-mula tahu tentang produk ini?',
    optionSeenAtStore: 'Nampak di kedai atau outlet',
    optionHeardFromSomeone: 'Diberitahu oleh kenalan',
    optionSeenOnline: 'Nampak di internet',
    optionSeenPackBefore: 'Pernah nampak pek produk ini sebelum ini',
    optionDontRemember: 'Tak ingat',
  },
  q7_2: {
    question: 'Sebelum mencuba produk ini, apakah yang biasa anda hisap?',
    diceQuestion: 'Apakah yang paling anda suka tentang produk ini?',
    optionTasteFlavour: 'Rasa atau perisa',
    optionOverallExperience: 'Pengalaman menggunakannya',
    optionAppearancePack: 'Rupa atau reka bentuk pek',
    optionSomethingDifferent: 'Kelainan daripada pilihan biasa',
    optionNothingInParticular: 'Tiada yang tertentu',
    yourAnswer: 'Jawapan Anda:',
    selectPlaceholder: 'Pilih jenama / varian',
    loading: 'Memuatkan jenama...',
    loadError: 'Tidak dapat memuatkan jenama. Sila cuba lagi.',
    requiredWarning: 'Sila pilih satu pilihan sebelum meneruskan.',
    others: 'Lain-lain',
    otherSpecifyPlaceholder: 'Sila nyatakan',
    otherSpecifyWarning: 'Sila nyatakan apa yang biasa anda hisap.',
    continue: 'Teruskan',
  },
  q7_3: {
    question: 'Apakah pilihan anda untuk pembelian seterusnya?',
    diceQuestion: 'Apakah yang boleh diperbaiki tentang produk ini?',
    optionChooseAgain: 'Beli produk ini lagi',
    optionGoBackUsual: 'Kembali kepada pilihan biasa',
    optionTryAnother: 'Cuba produk lain',
    optionDependOccasion: 'Bergantung pada keadaan',
    optionTasteFlavour: 'Rasa atau perisa',
    optionOverallExperience: 'Pengalaman menggunakannya',
    optionStrengthIntensity: 'Kekuatan produk',
    optionAppearancePack: 'Rupa atau reka bentuk pek',
    optionFormat: 'Bentuk',
    optionAvailability: 'Mudah didapati',
    optionNothingInParticular: 'Tiada yang tertentu',
  },
  q7_4: {
    question: 'Apakah yang paling anda suka tentang produk ini?',
    diceQuestion: 'Jika ditanya, sejauh mana anda mungkin berkongsi tentang produk ini?',
    optionTasteFlavour: 'Rasa atau perisa',
    optionOverallExperience: 'Pengalaman menggunakannya',
    optionAppearancePack: 'Rupa atau reka bentuk pek',
    optionSomethingDifferent: 'Kelainan daripada pilihan biasa',
    optionNothingInParticular: 'Tiada yang tertentu',
    optionVeryLikely: 'Sangat mungkin',
    optionSomewhatLikely: 'Agak mungkin',
    optionNotVeryLikely: 'Kurang mungkin',
    optionNotAtAllLikely: 'Langsung tidak mungkin',
    optionNotSure: 'Tidak pasti',
    submit: 'Hantar',
    selectWarning: 'Sila pilih satu pilihan sebelum menghantar.',
    sessionExpiredError: 'Sesi telah tamat tempoh. Sila mulakan semula dari langkah kod unik.',
    mobileError: 'Nombor telefon tiada. Sila mulakan semula dari langkah kod unik.',
    incompleteError: 'Data tinjauan tidak lengkap. Sila kembali dan lengkapkan langkah-langkah sebelumnya.',
    saveError: 'Tidak dapat menyimpan. Sila cuba lagi.',
  },
  q7_5: {
    question: 'Apakah yang boleh diperbaiki tentang produk ini?',
    optionTasteFlavour: 'Rasa atau perisa',
    optionOverallExperience: 'Pengalaman menggunakannya',
    optionStrengthIntensity: 'Kekuatan produk',
    optionAppearancePack: 'Rupa atau rupa bentuk pek',
    optionFormat: 'Bentuk produk',
    optionAvailability: 'Mudah didapati',
    optionNothingInParticular: 'Tiada yang tertentu',
  },
  q7_6: {
    question: 'Jika ditanya, sejauh mana anda mungkin berkongsi tentang produk ini?',
    optionVeryLikely: 'Sangat mungkin',
    optionSomewhatLikely: 'Agak mungkin',
    optionNotVeryLikely: 'Kurang mungkin',
    optionNotAtAllLikely: 'Langsung tidak mungkin',
    optionNotSure: 'Tidak pasti',
    submit: 'Hantar',
    selectWarning: 'Sila pilih satu pilihan sebelum menghantar.',
    sessionExpiredError: 'Sesi telah tamat tempoh. Sila mulakan semula dari langkah kod unik.',
    mobileError: 'Nombor telefon tiada. Sila mulakan semula dari langkah kod unik.',
    incompleteError: 'Data tinjauan tidak lengkap. Sila kembali dan lengkapkan langkah-langkah sebelumnya.',
    saveError: 'Tidak dapat menyimpan. Sila cuba lagi.',
  },
  devReg: {
    question: 'Adakan anda telah mendaftar peranti anda?', 
    optionYes: 'Ya.', 
    optionCareContact: 'Tidak, saya ingin Consumer Care Team menghubungi saya untuk pendaftaran peranti.', 
    optionNoRegister: 'Tidak, saya tidak mahu mendaftar peranti saya.', 
  },
  tradeIn: {
    question: 'Adakah anda ingin "trade-in" peranti anda?', 
    optionYesHelp: 'Ya, saya ingin Consumer Care Team membantu saya dalam proses "trade-in" peranti.', 
    optionNotInterested: 'Tidak, saya tidak berminat untuk "trade-in" peranti saya sekarang.', 
    optionAlreadyNew: 'Tidak, saya sudah menggunakan peranti baru.', 
    tnc: 'Tertakluk kepada terma dan syarat', 
    sessionExpiredError: 'Sesi telah tamat tempoh. Sila mulakan semula dari langkah kod unik.', 
    mobileError: 'Nombor telefon bimbit tiada. Sila kembali ke langkah kod unik.', 
    incompleteError: 'Data tinjauan tidak lengkap. Sila kembali dan lengkapkan langkah-langkah sebelumnya.', 
    saveError: 'Tidak dapat menyimpan. Sila cuba lagi.', 
  },
  tradeInContact: {
    instruction: 'Sila lengkapkan maklumat anda dalam ruangan di bawah.', 
    mobileLabel: 'Telefon No:', 
    nameLabel: 'Nama:', 
    emailLabel: 'Alamat Emel:', 
    submit: 'Hantar', 
    mobileWarning: 'Sila masukkan nombor telefon', 
    nameWarning: 'Sila masukkan nama', 
    emailWarning: 'Sila masukkan alamat emel', 
    sessionExpiredWarning: 'Sesi telah tamat tempoh. Sila mulakan semula dari langkah kod unik.', 
    incompleteWarning: 'Data tinjauan tidak lengkap. Sila kembali dan lengkapkan langkah-langkah sebelumnya.', 
    pdpaNote: '* Dengan menyerahkan maklumat peribadi anda, anda setuju untuk kami guna, kumpul dan proses data peribadi anda untuk program “trade-in” tertakluk kepada syarat notis PDPA kami', 
    saveError: 'Tidak dapat menyimpan. Sila cuba lagi.', 
  },
  thankYou: {
    title: 'TERIMA KASIH', 
    subtitle: 'ATAS PENYERTAAN ANDA', 
  },
  tng: {
    title: 'TERIMA KASIH', 
    subtitle: 'ATAS PENYERTAAN ANDA', 
    smsNote: 'Tekan ‘Salin’ untuk menyalin PIN TNG anda, kemudian tambah nilai eWallet Touch ’n Go anda.',
    copyLabel: 'salin',
    copyPin: 'Salin PIN',
    copiedPin: 'Disalin!',
  },
};

// ─── Chinese (placeholders — EN until CN copy is provided) ───────────────────

const cn: AppTranslations = {
  home: { ...en.home },
  ageSmoker: {
    title: '年龄',
    smokerQuestion: '您是否为吸烟者？',
    yes: '是',
    no: '否',
    submit: '提交',
    allRequiredWarning: '请先回答所有问题，再继续下一页。',
  },
  pdpa: {
    title: '个人数据保护声明（PDPA）',
    consentText: '本人特此确认同意按上述说明处理本人的个人数据。',
    agree: '同意',
    decline: '不同意',
    consentWarning: '请勾选条款与条件以继续。',
    noticeTitle: '2010年个人数据保护法（“PDPA”）通知',
  },
  uniqueCode: {
    instruction: '请输入您的手机号码及问卷唯一代码，以便我们向您发送验证码（OTP）。',
    mobileLabel: '手机号码：',
    uniqueCodeLabel: '唯一代码：',
    findCodeHint: '*请在二维码贴纸背面查找唯一代码。',
    requestOtpButton: '获取验证码',
    mobileWarning: '请输入您的手机号码。',
    uniqueCodeWarning: '请输入您的唯一代码。',
    codeUsedWarning: '此唯一代码已被兑换。',
    codeNotFoundWarning: '找不到唯一代码',
    verifyError: '无法验证唯一代码，请重试。',
    otpError: '无法发送验证码，请重试。',
    turnstileFailed: '安全验证未能完成。请检查网络连接后重试。',
    confirmTitle: '您已输入：',
    confirmMobileLabel: '手机号码：',
    confirmUniqueCodeLabel: '唯一代码：',
    confirmPrompt: '请确认信息正确。',
    verifyingSecurity: '正在进行安全验证，请稍候…',
    back: '返回',
    confirm: '确认',
    monthlyLimitMessage:
      '感谢您参与我们的问卷！您本月的参与次数已达上限。请下个月再来参与。',
    monthlyLimitClose: '关闭',
    dailyLimitMessage: '此手机号码今日兑换次数已达上限。',
  },
  otp: {
    sentMessage: '验证码已发送至您的手机号码：',
    otpCodeLabel: '验证码：',
    submit: '提交',
    emptyWarning: '请输入验证码。',
    verifyError: '无法验证验证码，请重试。',
    waitBeforeSubmit:
      '请等待 {{seconds}} 秒后再点击提交。等待期间可输入验证码 — 请勿反复点击提交。',
    readyToSubmit: '现在可以点击提交。',
  },
  smokerProfile: {
    nameLabel: '姓名',
    namePlaceholder: '请输入您的姓名',
    emailLabel: '电邮',
    emailPlaceholder: '请输入您的电邮',
    emailWarning: '请输入有效的电邮地址。',
    nationalityLabel: '国籍',
    selectNationality: '请选择国籍',
    nationalityMalaysian: '马来西亚籍',
    nationalityNonMalaysian: '非马来西亚籍',
    countryLabel: '国家',
    selectCountry: '请选择国家',
    raceLabel: '种族',
    stateLabel: '居住州属',
    selectRace: '请选择种族',
    selectState: '请选择州属',
    raceMalay: '马来人',
    raceChinese: '华人',
    raceIndian: '印度人',
    raceOthers: '其他',
    stateJohor: '柔佛',
    stateKedah: '吉打',
    stateKelantan: '吉兰丹',
    stateKualaLumpur: '吉隆坡',
    stateMelaka: '马六甲',
    stateNegeriSembilan: '森美兰',
    statePahang: '彭亨',
    statePerak: '霹雳',
    statePerlis: '玻璃市',
    statePulauPinang: '槟城',
    stateSabah: '沙巴',
    stateSarawak: '砂拉越',
    stateSelangor: '雪兰莪',
    stateTerengganu: '登嘉楼',
    brandLabel: '您目前抽的品牌',
    selectBrand: '请选择品牌',
    loadingBrands: '加载中…',
    selectBrandFirst: '请先选择品牌',
    selectVariant: '请选择系列',
    loadingVariants: '加载中…',
    brandsLoadingWarning: '请等待品牌列表加载完成。',
    variantsLoadingWarning: '请等待系列列表加载完成。',
    brandsLoadError: '无法加载品牌，请重试。',
    continue: '继续',
    incompleteWarning: '请填写所有必填资料后再继续。',
  },
  otherBrands: {
    question: '您之前抽的是哪个品牌？',
    loading: '正在加载品牌…',
    loadError: '无法加载品牌，请重试。',
    requiredWarning: '请先选择品牌系列再继续。',
    otherSpecify: '其他，请注明：',
    otherSpecifyWarning: '请注明您的品牌。',
    continue: '继续',
  },
  q7_1: {
    question: '您最初是通过什么途径了解到这款产品的？',
    diceQuestion: '您最初是通过什么途径了解到这款产品的？',
    optionSeenAtStore: '在商店或零售店看到',
    optionHeardFromSomeone: '听身边的人提起',
    optionSeenOnline: '在网上看到',
    optionSeenPackBefore: '之前见过这款产品或它的包装',
    optionDontRemember: '不记得了',
  },
  q7_2: {
    ...en.q7_2,
    question: '在尝试这款产品之前，您平时抽什么产品？',
    diceQuestion: '您最喜欢这款产品的哪些方面？',
    optionTasteFlavour: '口感或味道',
    optionOverallExperience: '整体体验',
    optionAppearancePack: '产品外观或包装',
    optionSomethingDifferent: '与平时选择的产品有所不同',
    optionNothingInParticular: '没有特别喜欢的地方',
    yourAnswer: '您的回答：',
    selectPlaceholder: '请选择品牌 / 系列',
    loading: '正在加载品牌…',
    loadError: '无法加载品牌，请重试。',
    requiredWarning: '请先选择一个选项再继续。',
    others: '其他',
    otherSpecifyPlaceholder: '请注明',
    otherSpecifyWarning: '请注明您平时抽的产品。',
    continue: '继续',
  },
  q7_3: {
    ...en.q7_3,
    question: '下次购买时，您更倾向于哪种选择？',
    diceQuestion: '您觉得这款产品有哪些方面可以改进？',
    optionChooseAgain: '再次购买这款产品',
    optionGoBackUsual: '继续购买平时常用的产品',
    optionTryAnother: '尝试其他产品',
    optionDependOccasion: '视情况而定',
    optionTasteFlavour: '口感或味道',
    optionOverallExperience: '整体体验',
    optionStrengthIntensity: '烟气的浓淡或劲道',
    optionAppearancePack: '产品外观或包装',
    optionFormat: '产品形式',
    optionAvailability: '产品是否容易买到',
    optionNothingInParticular: '没有需要改进的地方',
  },
  q7_4: {
    ...en.q7_4,
    question: '您最喜欢这款产品的哪些方面？',
    diceQuestion: '如果有人问起这款产品，您有多大可能会向对方介绍这款产品？',
    optionTasteFlavour: '口感或味道',
    optionOverallExperience: '整体体验',
    optionAppearancePack: '产品外观或包装',
    optionSomethingDifferent: '与平时选择的产品有所不同',
    optionNothingInParticular: '没有特别喜欢的地方',
    optionVeryLikely: '非常有可能',
    optionSomewhatLikely: '比较有可能',
    optionNotVeryLikely: '不太可能',
    optionNotAtAllLikely: '完全不可能',
    optionNotSure: '不确定',
    submit: '提交',
    selectWarning: '请先选择一个选项再提交。',
  },
  q7_5: {
    ...en.q7_5,
    question: '您觉得这款产品有哪些方面可以改进？',
    optionTasteFlavour: '口感或味道',
    optionOverallExperience: '整体体验',
    optionStrengthIntensity: '烟气的浓淡或劲道',
    optionAppearancePack: '产品外观或包装',
    optionFormat: '产品形式',
    optionAvailability: '产品是否容易买到',
    optionNothingInParticular: '没有需要改进的地方',
  },
  q7_6: {
    ...en.q7_6,
    question: '如果有人问起这款产品，您有多大可能会向对方介绍这款产品？',
    optionVeryLikely: '非常有可能',
    optionSomewhatLikely: '比较有可能',
    optionNotVeryLikely: '不太可能',
    optionNotAtAllLikely: '完全不可能',
    optionNotSure: '不确定',
    submit: '提交',
    selectWarning: '请先选择一个选项再提交。',
  },
  devReg: { ...en.devReg },
  tradeIn: { ...en.tradeIn },
  tradeInContact: { ...en.tradeInContact },
  thankYou: { ...en.thankYou },
  tng: {
    title: '感谢您',
    subtitle: '参与本问卷',
    smsNote: '点击「复制」TNG PIN码，并在 Touch ’n Go eWallet 中充值。',
    copyLabel: '复制',
    copyPin: '复制 PIN',
    copiedPin: '已复制！',
  },
};

// ─── Resolver ────────────────────────────────────────────────────────────────

/**
 * Returns translations for the given language.
 * Any BM/CN string that is empty falls back to the English equivalent
 * so the app remains fully functional while translation copy is being filled in.
 */
function fallback<T extends Record<string, string>>(section: T, enSection: T): T {
  return Object.fromEntries(
    Object.entries(section).map(([k, v]) => [k, v || (enSection as Record<string, string>)[k]])
  ) as T;
}

function resolveLocalized(localized: AppTranslations): AppTranslations {
  return {
    home:            fallback(localized.home, en.home),
    ageSmoker:       fallback(localized.ageSmoker, en.ageSmoker),
    pdpa:            fallback(localized.pdpa, en.pdpa),
    uniqueCode:      fallback(localized.uniqueCode, en.uniqueCode),
    otp:             fallback(localized.otp, en.otp),
    smokerProfile:   fallback(localized.smokerProfile, en.smokerProfile),
    otherBrands:     fallback(localized.otherBrands, en.otherBrands),
    q7_1:            fallback(localized.q7_1, en.q7_1),
    q7_2:            fallback(localized.q7_2, en.q7_2),
    q7_3:            fallback(localized.q7_3, en.q7_3),
    q7_4:            fallback(localized.q7_4, en.q7_4),
    q7_5:            fallback(localized.q7_5, en.q7_5),
    q7_6:            fallback(localized.q7_6, en.q7_6),
    devReg:          fallback(localized.devReg, en.devReg),
    tradeIn:         fallback(localized.tradeIn, en.tradeIn),
    tradeInContact:  fallback(localized.tradeInContact, en.tradeInContact),
    thankYou:        fallback(localized.thankYou, en.thankYou),
    tng:             fallback(localized.tng, en.tng),
  };
}

export function getTranslations(lang: Lang): AppTranslations {
  if (lang === 'en') return en;
  if (lang === 'cn') return resolveLocalized(cn);
  return resolveLocalized(bm);
}
